import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../Dropdown/LocationName';
import AreaDropdown from '../../Dropdown/AreaDropdown';
import MedicalDropdown from '../../Dropdown/MedicalDropdown';

const EmployeeAddModal = ({
  show,
  onClose,
  formData,
  onChange,
  onSave,
  isEditMode,
  isLoading,
}) => {
  const [errors, setErrors] = useState({});
  const [isAreaValid, setIsAreaValid] = useState(false);
  const [selectionType, setSelectionType] = useState('location');

  // Session data
  const sessionData = {
    roleName: sessionStorage.getItem('Role_Name'),
    medicalCenterId: sessionStorage.getItem('Medical_Center_Id'),
    medicalCenterName: sessionStorage.getItem('Medical_Center_Name'),
  };

  const isResourceManager = sessionData.roleName === 'EMS Resource Manager';

  // Condition 1: Force Medical Center only (Location hidden)
  const forceMedicalCenterOnly =
    isResourceManager &&
    sessionData.medicalCenterId === '0' &&
    (!sessionData.medicalCenterName || sessionData.medicalCenterName.trim() === '');

  // Condition 2: Manager belongs to a specific medical center
  const isSpecificMedicalCenter =
    isResourceManager &&
    sessionData.medicalCenterId &&
    sessionData.medicalCenterId !== '0' &&
    sessionData.medicalCenterId !== '[]' &&
    sessionData.medicalCenterId !== '';

  // Auto-set selection type on mount or when session/form changes
  useEffect(() => {
    if (!show) return;

    if (forceMedicalCenterOnly) {
      setSelectionType('medical');
      // Clear location/area
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
    } else if (isSpecificMedicalCenter) {
      setSelectionType('medical');
      onChange({ target: { name: 'medical_center_id', value: sessionData.medicalCenterId } });
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
    } else if (formData.medical_center_id) {
      setSelectionType('medical');
    } else if (formData.location_id) {
      setSelectionType('location');
    } else {
      setSelectionType('location'); // default
    }

    setErrors({});
    setIsAreaValid(!!(formData.location_id && formData.area_id));
  }, [
    show,
    forceMedicalCenterOnly,
    isSpecificMedicalCenter,
    formData.medical_center_id,
    formData.location_id,
    formData.area_id,
  ]);

  const handleClose = () => {
    sessionStorage.removeItem('area_id');
    sessionStorage.removeItem('location_id');
    onClose();
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone.replace(/\D/g, ''));

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange(e);
    setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'email' && value && !validateEmail(value)) {
      setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
    }

    if (name === 'phone_number') {
      const cleaned = value.replace(/\D/g, '').slice(0, 10);
      onChange({ target: { name, value: cleaned } });
      if (cleaned && !validatePhone(cleaned)) {
        setErrors((prev) => ({ ...prev, phone_number: 'Phone must be 10 digits' }));
      } else {
        setErrors((prev) => ({ ...prev, phone_number: '' }));
      }
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    onChange(e);
    onChange({ target: { name: 'area_id', value: '' } });
    setIsAreaValid(false);

    setErrors((prev) => ({
      ...prev,
      location_id: value ? '' : 'Please select a location',
      area_id: value ? prev.area_id : 'Please select a location first',
    }));
  };

  const handleAreaChange = (e, isValid) => {
    onChange(e);
    setIsAreaValid(isValid);
    if (isValid && e.target.value) {
      setErrors((prev) => ({ ...prev, area_id: '' }));
    }
  };

  const handleAreaValidationChange = (isValid) => {
    setIsAreaValid(isValid);
    if (!isValid && formData.location_id && formData.area_id) {
      setErrors((prev) => ({ ...prev, area_id: 'Please select a valid area' }));
    } else {
      setErrors((prev) => ({ ...prev, area_id: '' }));
    }
  };

  const handleSelectionTypeChange = (e) => {
    const type = e.target.value;

    // Block switching to location if forced to medical only
    if (forceMedicalCenterOnly && type === 'location') return;
    if (isSpecificMedicalCenter && type === 'location') return;

    setSelectionType(type);

    if (type === 'location') {
      onChange({ target: { name: 'medical_center_id', value: '' } });
    } else {
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
      setIsAreaValid(false);
    }

    setErrors((prev) => ({
      ...prev,
      location_id: '',
      area_id: '',
      medical_center_id: '',
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Required fields
    const baseFields = ['emp_id', 'first_name', 'last_name', 'email', 'phone_number'];
    baseFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });

    if (selectionType === 'location') {
      if (!formData.location_id) newErrors.location_id = 'Please select a location';
      if (formData.location_id && (!formData.area_id || !isAreaValid)) {
        newErrors.area_id = 'Please select a valid area';
      }
    } else {
      if (!formData.medical_center_id || formData.medical_center_id === '0') {
        newErrors.medical_center_id = 'Please select a medical center';
      }
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.phone_number && !validatePhone(formData.phone_number)) {
      newErrors.phone_number = 'Phone must be 10 digits';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onSave();
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header d-flex align-items-center">
              <h5 className="modal-title">
                {isEditMode ? 'Edit Employee' : 'Add Employee'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>
                    Employee ID <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="emp_id"
                    value={formData.emp_id || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.emp_id ? 'is-invalid' : ''}`}
                    disabled={isEditMode}
                  />
                  {errors.emp_id && <div className="invalid-feedback">{errors.emp_id}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label>Speciality</label>
                  <input
                    type="text"
                    value="Paramedic"
                    className="form-control"
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                  />
                  {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                  />
                  {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label>
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="phone_number"
                    value={formData.phone_number || ''}
                    onChange={handleChange}
                    className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`}
                    maxLength={10}
                    placeholder="10 digit number"
                  />
                  {errors.phone_number && <div className="invalid-feedback">{errors.phone_number}</div>}
                </div>
              </div>

              {/* Selection Type: Location vs Medical Center */}
              <div className="row mb-3">
                <div className="col-12">
                  <label className="me-3">
                    {forceMedicalCenterOnly || isSpecificMedicalCenter
                      ? 'Medical Center'
                      : 'Choose Location or Medical Center'} <span className="text-danger">*</span>
                  </label>

                  {/* Hide Location radio when forced to Medical Center */}
                  {!forceMedicalCenterOnly && !isSpecificMedicalCenter && (
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="selectionType"
                        id="locationRadio"
                        value="location"
                        checked={selectionType === 'location'}
                        onChange={handleSelectionTypeChange}
                      />
                      <label className="form-check-label" htmlFor="locationRadio">
                        Location
                      </label>
                    </div>
                  )}

                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="selectionType"
                      id="medicalRadio"
                      value="medical"
                      checked={selectionType === 'medical'}
                      onChange={handleSelectionTypeChange}
                      disabled={forceMedicalCenterOnly || isSpecificMedicalCenter}
                    />
                    <label className="form-check-label" htmlFor="medicalRadio">
                      Medical Center
                    </label>
                  </div>
                </div>
              </div>

              {/* Conditional Fields */}
              {selectionType === 'location' ? (
                <>
                <div className='row'>
                  <div className="col-md-6 mb-3">
                    <label>
                      Location <span className="text-danger">*</span>
                    </label>
                    <LocationDropdown
                      value={formData.location_id || ''}
                      onChange={handleLocationChange}
                      className={errors.location_id ? 'is-invalid' : ''}
                    />
                    {errors.location_id && (
                      <div className="text-danger">{errors.location_id}</div>
                    )}
                  </div>

                  <div className=" col-md-6 mb-3">
                    <label>
                      Area <span className="text-danger">*</span>
                    </label>
                    <AreaDropdown
                      locationId={formData.location_id}
                      value={formData.area_id || ''}
                      onChange={handleAreaChange}
                      onValidationChange={handleAreaValidationChange}
                    />
                    {errors.area_id && <div className="text-danger">{errors.area_id}</div>}
                  </div>
                  </div>
                </>
              ) : (
                <div className=" col-md-6 mb-3">
                  <label>
                    Medical Center <span className="text-danger">*</span>
                  </label>
                  <MedicalDropdown
                    value={
                      formData.medical_center_id ||
                      (isSpecificMedicalCenter ? sessionData.medicalCenterId : '')
                    }
                    onChange={(e) => {
                      onChange(e);
                      setErrors((prev) => ({ ...prev, medical_center_id: '' }));
                    }}
                    disabled={isSpecificMedicalCenter}
                  />
                  {errors.medical_center_id && (
                    <div className="text-danger">{errors.medical_center_id}</div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {isEditMode ? 'Updating...' : 'Saving...'}
                  </>
                ) : isEditMode ? (
                  'Update'
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAddModal;